import SEO from "../components/seo.jsx"
import { SITE_ORIGIN } from "../siteConfig";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroImg from "../assets/male-hair-loss.png";

gsap.registerPlugin(ScrollTrigger);

const cinzel = { fontFamily: "'Cinzel', serif" };

const causes = [
  {
    title: "Androgens",
    desc: "Testosterone and Di-hydro-testosterone (DHT) are the most important hormonal factors driving male pattern hair loss, shrinking follicles over time.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <circle cx="30" cy="28" r="14" stroke="#c9a84c" strokeWidth="2.5" fill="none"/>
        <path d="M40 18l10-10M50 8h-8M50 8v8" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M30 42v14M22 50h16" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Genetics",
    desc: "Male pattern hair loss has a strong genetic component, easily traced through the patient's family history on both maternal and paternal sides.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <path d="M24 10c0 8 16 8 16 16s-16 8-16 16" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M40 10c0 8-16 8-16 16s16 8 16 16" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M20 10h24M20 42h24" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
        <path d="M28 52v4M36 52v4M28 8V4M36 8V4" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "DHT Sensitivity",
    desc: "Genetically susceptible follicles become sensitive to DHT, leading to progressive miniaturisation of hair shafts and eventual hair loss.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <circle cx="32" cy="32" r="18" stroke="#c9a84c" strokeWidth="2.5" fill="none"/>
        <path d="M32 22v10l6 6" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="32" cy="32" r="3" fill="#c9a84c"/>
      </svg>
    ),
  },
  {
    title: "Age & Hormones",
    desc: "As men age, hormonal fluctuations accelerate follicular miniaturisation, causing the hairline to recede and the crown to thin progressively.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <path d="M16 48c2-10 6-18 16-24 10 6 14 14 16 24" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M32 24V12" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M26 14l6-6 6 6" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 48h20" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const treatments = [
  {
    step: "01",
    title: "Consultation & Diagnosis",
    desc: "A thorough evaluation including family history, scalp examination, and trichoscopy to determine the pattern, grade, and progression of hair loss.",
  },
  {
    step: "02",
    title: "Medical Management",
    desc: "Clinically proven medications such as Minoxidil and Finasteride are prescribed to slow further loss and stabilise the scalp environment.",
  },
  {
    step: "03",
    title: "PRP Therapy",
    desc: "Platelet-Rich Plasma injections stimulate dormant follicles, improve blood supply to the scalp, and enhance overall hair density non-surgically.",
  },
  {
    step: "04",
    title: "Hair Transplant (FUE/FUT)",
    desc: "For permanent restoration, our surgeons perform FUE or FUT transplantation, harvesting donor grafts and placing them with precision in thinning zones.",
  },
];

const norwood = [
  { stage: "I", desc: "Minimal recession" },
  { stage: "II", desc: "Slight temples recession" },
  { stage: "III", desc: "Deep temporal recession" },
  { stage: "IV", desc: "Crown thinning begins" },
  { stage: "V", desc: "Crown & front merge" },
  { stage: "VI", desc: "Sparse bridge remains" },
  { stage: "VII", desc: "Only sides remain" },
];

const faqs = [
  {
    q: "What is androgenetic alopecia?",
    a: "Androgenetic alopecia, commonly known as male pattern baldness, is a hereditary condition driven by the hormone DHT. It follows a predictable pattern of hairline recession and crown thinning.",
  },
  {
    q: "Can male pattern hair loss be stopped completely?",
    a: "While it cannot be permanently stopped without surgical intervention, early medical management with Minoxidil and Finasteride can significantly slow progression and preserve existing hair.",
  },
  {
    q: "At what age should I seek treatment?",
    a: "The sooner treatment begins, the better the outcomes. Many patients benefit from starting medical therapy in their 20s. A consultation helps determine the right timing for each individual.",
  },
  {
    q: "Is hair transplant the only permanent solution?",
    a: "Hair transplant surgery (FUE/FUT) remains the only permanent, surgical solution. It relocates genetically resistant follicles from the donor zone to thinning areas.",
  },
  {
    q: "How do I know if I am a good candidate for a hair transplant?",
    a: "Good candidates typically have stable hair loss, adequate donor density at the back and sides, and realistic expectations. Our specialists assess this in detail during your consultation.",
  },
];

/* ─────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────── */
import { useState } from "react";

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/15 bg-white"}`}
    >
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={() => setOpen((p) => !p)}
      >
        <span style={cinzel} className="text-[13px] font-bold tracking-[0.04em] text-[#1a0f02]">{q}</span>
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-[13.5px] leading-[1.8] text-[#6b5535]">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function MaleHairLoss() {
  const pageRef        = useRef(null);
  const heroEyeRef     = useRef(null);
  const heroHeadRef    = useRef(null);
  const heroSubRef     = useRef(null);
  const introEyeRef    = useRef(null);
  const introHeadRef   = useRef(null);
  const introBody1Ref  = useRef(null);
  const introBody2Ref  = useRef(null);
  const introBody3Ref  = useRef(null);
  const causesRowRef   = useRef(null);
  const causeItemsRef  = useRef([]);
  const norwoodRef     = useRef(null);
  const norItemsRef    = useRef([]);
  const treatRowRef    = useRef(null);
  const treatItemsRef  = useRef([]);
  const faqRef         = useRef(null);
  const ctaRef         = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";

      const fadeUp = (el, delay = 0, trigger) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.75, ease, delay,
            scrollTrigger: { trigger: trigger || el, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      };

      // hero
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      // intro
      fadeUp(introEyeRef.current);
      fadeUp(introHeadRef.current, 0.06);
      fadeUp(introBody1Ref.current, 0.1);
      fadeUp(introBody2Ref.current, 0.14);
      fadeUp(introBody3Ref.current, 0.18);

      // cause cards
      causeItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, y: 36 },
          { opacity: 1, y: 0, duration: 0.65, ease, delay: i * 0.1,
            scrollTrigger: { trigger: causesRowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      // norwood scale
      norItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: 0.5, ease, delay: i * 0.07,
            scrollTrigger: { trigger: norwoodRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      // treatment steps
      treatItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -30 : 30 },
          { opacity: 1, x: 0, duration: 0.7, ease, delay: i * 0.08,
            scrollTrigger: { trigger: treatRowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      fadeUp(faqRef.current);
      fadeUp(ctaRef.current);

    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <>

 <SEO
      title="Male Hair Loss Causes & Treatment | Clinique Internationale"
      description="Understand androgenetic alopecia, the Norwood scale, and treatment pathways for male hair loss — from medical management to FUE/FUT hair transplant."
      url={`${SITE_ORIGIN}/male-hair-loss.php`}
    />

      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

      <div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 sm:mt-20 lg:mt-30 " style={{ fontFamily: "'Raleway', sans-serif" }}>

        {/* ═══════════════════ HERO BANNER ═══════════════════ */}
<div className="relative h-[220px] sm:h-[280px] md:h-[600px] flex items-center justify-end overflow-hidden">
          <img
            className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
            src={HeroImg}
            alt="Male hair loss treatment at Clinique Internationale"
          />
          <div className="absolute inset-0 " />

          {/* grain overlay */}
          <div className="absolute inset-0 opacity-40 pointer-events-none"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
          />

          <div className="relative z-10 text-right px-6 sm:px-10 md:px-16 max-w-xl">
            <p
              ref={heroEyeRef}
              style={cinzel}
              className="text-[4px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#c9a84c] mb-4 flex items-center justify-end gap-2.5"
            >
              <span className="w-5 sm:w-10 h-px bg-[#c9a84c]" />
              Hair Loss Treatments
              <span className="w-5 sm:w-10 h-px bg-[#c9a84c]" />
            </p>
            <h1
              ref={heroHeadRef}
              style={cinzel}
              className="font-black text-[0.8rem] sm:text-[2.6rem] md:text-[4rem] leading-[1.15] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-3 leading-tight"
            >
              Male Hair Loss
            </h1>
            <p ref={heroSubRef} className="text-[8px] sm:text-[15px] text-white/70 leading-relaxed ml-auto">
              Understanding androgenetic alopecia causes, patterns and pathways to lasting restoration.
            </p>
          </div>
        </div>

        {/* ─── breadcrumb ─── */}
        <div className="bg-[#0e0905]/95 border-b border-[#c9a84c]/10">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex items-center gap-2">
            <a href="/" style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">Home</a>
            <span className="text-[#c9a84c]/30 text-xs">›</span>
            <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">Male Hair Loss</span>
          </div>
        </div>

        {/* ═══════════════════ INTRO / OVERVIEW ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-20 md:pt-24 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">

            {/* LEFT text */}
            <div>
              <p ref={introEyeRef} style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center gap-2.5">
                <span className="w-6 h-px bg-[#c9a84c]" />
                Androgenetic Alopecia
              </p>
              <h2 ref={introHeadRef} style={cinzel} className="font-black text-[1.55rem] sm:text-[1.85rem] md:text-[2.1rem] tracking-[0.03em] uppercase text-[#1a0f02] leading-tight mb-6">
                Understanding<br />Male Pattern Hair Loss
              </h2>
              <p ref={introBody1Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                The role of androgens in male pattern hair loss has been considered since ancient times.
                <strong className="font-semibold text-[#1a0f02]"> Testosterone and Di-hydro-testosterone (DHT)</strong> have
                been implicated as the most important hormonal factors for male pattern hair loss.
              </p>
              <p ref={introBody2Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                Most of the patients we encounter for hair transplant are typical cases of androgenetic
                alopecia. Male pattern hair loss has a strong genetic component and is easily elicited
                from the patient's family history.
              </p>
              <p ref={introBody3Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e]">
                Therefore, we evaluate the <strong className="font-semibold text-[#1a0f02]">complete family history</strong> of the
                patient before deciding any kind of treatment plan, ensuring the most accurate and
                personalised approach to restoration.
              </p>
            </div>

            {/* RIGHT key fact card */}
            <div className="flex flex-col gap-5">
              {/* stat highlight */}
              <div className="relative overflow-hidden bg-[#0e0905] border border-[#c9a84c]/20 rounded-2xl p-7">
                <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/50 to-transparent" />
                <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 mb-5">At a Glance</p>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { val: "85%", sub: "of men affected by age 50" },
                    { val: "DHT", sub: "primary hormonal driver" },
                    { val: "10,000+", sub: "cases treated by us" },
                    { val: "95%+", sub: "transplant graft survival" },
                  ].map((s) => (
                    <div key={s.val}>
                      <div style={cinzel} className="font-black text-[1.6rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">
                        {s.val}
                      </div>
                      <div className="text-[11.5px] text-white/45 leading-snug">{s.sub}</div>
                    </div>
                  ))}
                </div>
                <span className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/20 to-transparent" />
              </div>

              {/* doctor note card */}
              <div className="relative overflow-hidden bg-white border border-[#c9a84c]/20 rounded-2xl p-6">
                <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                <div className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c]/15 to-[#c9a84c]/5 border border-[#c9a84c]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                      <circle cx="12" cy="8" r="4" stroke="#c9a84c" strokeWidth="1.8" fill="none"/>
                      <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="#c9a84c" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <p style={cinzel} className="text-[11px] font-bold tracking-[0.06em] text-[#1a0f02] mb-1">Dr. Bhavesh Gupta</p>
                    <p className="text-[12.5px] italic leading-[1.75] text-[#6b5535]">
                      "Understanding a patient's hormonal profile and genetic background allows us to predict
                      the course of hair loss accurately and design a plan that delivers lasting results."
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ═══════════════════ CAUSES GRID ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
            <span className="w-6 h-px bg-[#c9a84c]" />
            Root Factors
          </p>
          <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-10 sm:mb-12">
            Key Causes of Male Hair Loss
          </h3>
          <div ref={causesRowRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
            {causes.map((c, i) => (
              <div
                key={i}
                ref={(el) => (causeItemsRef.current[i] = el)}
                className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_16px_36px_rgba(90,60,10,0.1)] transition-all duration-300"
              >
                <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#c9a84c]/10 to-[#c9a84c]/[0.03] border border-[#c9a84c]/20 flex items-center justify-center mb-5">
                  {c.icon}
                </div>
                <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-2.5">
                  {c.title}
                </div>
                <p className="text-[13px] leading-[1.75] text-[#6b5535]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════ NORWOOD SCALE ═══════════════════ */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
        <div className="bg-[#faf8f4] py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]" />
              Classification
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-3">
              The Norwood Scale
            </h3>
            <p className="text-[14px] text-center text-[#6b5535] max-w-xl mx-auto mb-10 sm:mb-12">
              We use the Norwood-Hamilton classification to map the extent of hair loss and plan the most effective treatment strategy for each patient.
            </p>
            <div ref={norwoodRef} className="grid grid-cols-4 sm:grid-cols-7 gap-3 sm:gap-4">
              {norwood.map((n, i) => (
                <div
                  key={i}
                  ref={(el) => (norItemsRef.current[i] = el)}
                  className="flex flex-col items-center text-center"
                >
                  <div
                    className="w-full aspect-square rounded-xl border flex items-center justify-center mb-2.5 relative overflow-hidden transition-all duration-300 hover:border-[#c9a84c]/50 hover:shadow-[0_6px_20px_rgba(201,168,76,0.15)]"
                    style={{ background: `rgba(201,168,76,${0.04 + i * 0.035})`, borderColor: `rgba(201,168,76,${0.15 + i * 0.05})` }}
                  >
                    <span style={cinzel} className="font-black text-[1.5rem] sm:text-[1.8rem] text-[#1a0f02]">{n.stage}</span>
                    {/* progressive hair loss visual bars */}
                    <div className="absolute bottom-1.5 left-1.5 right-1.5 flex gap-[2px]">
                      {Array.from({ length: 7 }).map((_, j) => (
                        <div
                          key={j}
                          className="flex-1 rounded-full"
                          style={{
                            height: "3px",
                            background: j < (7 - i) ? "#c9a84c" : "rgba(201,168,76,0.15)",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] sm:text-[11px] text-[#6b5535] leading-snug">{n.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-[12px] text-[#a07828] mt-6">
              Stage I (minimal) → Stage VII (advanced) · Identified during your initial consultation
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />

        {/* ═══════════════════ TREATMENT STEPS ═══════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#c9a84c]/70 mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]/50" />
              Our Approach
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-white uppercase tracking-[0.06em] mb-10 sm:mb-14">
              Treatment Pathway
            </h3>
            <div ref={treatRowRef} className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {treatments.map((t, i) => (
                <div
                  key={i}
                  ref={(el) => (treatItemsRef.current[i] = el)}
                  className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/15 rounded-2xl p-7 hover:border-[#c9a84c]/30 hover:bg-[#c9a84c]/[0.1] transition-all duration-300"
                >
                  <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                  <div className="flex items-start gap-5">
                    <span style={cinzel} className="font-black text-[2.2rem] leading-none text-[#c9a84c]/20 select-none flex-shrink-0">
                      {t.step}
                    </span>
                    <div>
                      <div style={cinzel} className="text-[13.5px] font-bold tracking-[0.06em] uppercase text-white/90 mb-2.5">
                        {t.title}
                      </div>
                      <p className="text-[13px] leading-[1.8] text-white/55">{t.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════ FAQs ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 md:py-24">
          <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
            <span className="w-6 h-px bg-[#c9a84c]" />
            Common Questions
          </p>
          <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-10">
            Frequently Asked Questions
          </h3>
          <div ref={faqRef} className="max-w-3xl mx-auto flex flex-col gap-3">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>

        {/* ═══════════════════ MISSION STRIP ═══════════════════ */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
        <div className="bg-[#0e0905] py-14 sm:py-16">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <p style={cinzel} className="text-[1rem] sm:text-[1.2rem] md:text-[1.4rem] font-light leading-[1.7] text-white/85 tracking-[0.02em]">
              "We evaluate the{" "}
              <em className="italic" style={{ color: "#c9a84c" }}>complete family history</em>{" "}
              of every patient before deciding any treatment plan because precision in diagnosis
              is the first step to lasting restoration."
            </p>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#c9a84c]/50 mt-5">
             Dr. Bhavesh Gupta, Founder · Clinique Internationale
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />

        {/* ═══════════════════ CTA ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 md:py-24">
          <div ref={ctaRef} className="max-w-xl mx-auto text-center">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]" />
              Take the First Step
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">
              Speak With Our Specialists
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">
              Whether you're just noticing thinning or have been dealing with significant loss for years,
              we'll build a treatment plan tailored specifically to you.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href="/khargharNaviMumbai.php"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                Book Consultation
              </a>
              <a
                href="/about-hair-transplants.php"
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] cursor-pointer hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                View All Treatments
              </a>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}